import {
  Box,
  Dropdown,
  Inline,
  type OptionType,
  type SingleValue,
} from '@omnipresentgroup/design-system';
import { toSentenceCase } from '@omnipresentgroup/helpers';
import {
  CountrySelector,
  ManagerEmployeeSelector,
} from '@omnipresentgroup/shared';
import type { Benefit } from '@omnipresentgroup/types';
import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router';

export const BENEFIT_TYPE_PARAM = 'benefitType';

export const BenefitFilters = ({
  benefits,
  companyId,
  enable,
}: {
  benefits?: Benefit[];
  companyId?: string;
  enable: {
    employee?: boolean;
    country?: boolean;
    benefitType?: boolean;
  };
}) => {
  const [searchParameters, setSearchParameters] = useSearchParams();

  const [supportedBenefitTypes, setSupportedBenefitTypes] = useState<
    OptionType[]
  >([]);

  const benefitType = new URLSearchParams(searchParameters).get(
    BENEFIT_TYPE_PARAM,
  ) as string;

  useEffect(() => {
    if (benefits && supportedBenefitTypes.length === 0) {
      setSupportedBenefitTypes([
        ...new Map(
          benefits.map((benefit: Benefit) => [
            benefit.benefitType,
            {
              label: toSentenceCase(benefit.benefitType),
              value: benefit.benefitType,
            },
          ]),
        ).values(),
      ]);
    }
  }, [benefits, supportedBenefitTypes.length]);

  return (
    <Inline>
      {enable.employee && companyId && (
        <Box minW={350}>
          <ManagerEmployeeSelector
            companyId={companyId}
            label="Employee"
            size="medium"
          />
        </Box>
      )}
      {enable.country && (
        <Box minW={200}>
          <CountrySelector label="Country" size="medium" />
        </Box>
      )}
      {enable.benefitType && (
        <Box minW={250}>
          <Dropdown
            id="benefit-type-filter"
            label="Benefit type"
            isMulti={false}
            options={supportedBenefitTypes}
            value={supportedBenefitTypes?.find(
              (option) => option.value === benefitType,
            )}
            onChange={(option, actionMeta) => {
              if (actionMeta.action === 'clear') {
                setSearchParameters((parameters) => {
                  parameters.delete(BENEFIT_TYPE_PARAM);
                  return parameters;
                });
              } else {
                setSearchParameters((parameters) => {
                  parameters.set(
                    BENEFIT_TYPE_PARAM,
                    (option as SingleValue<OptionType>)?.value as string,
                  );
                  return parameters;
                });
              }
            }}
            isClearable
            isSearchable
            placeholder="Search by benefit type"
          />
        </Box>
      )}
    </Inline>
  );
};
